import { createSlice, PayloadAction, PrepareAction } from '@reduxjs/toolkit'

interface Release {
  releaseid: string | number
  [key: string]: unknown
}

interface ReleasesState {
  queryKey: string | null
  requestId: string | null
  archiveToFilterEnabled: boolean
  queryArchived: boolean
  pendingArchiveIds: string[]
  page: number
  totalItemsCount: number | null
  sort: string | null
  filter: string | null
  artist: string | null
  title: string | null
  label: string | null
  version: string | null
  market: string | null
  days: string | null
  loading: boolean
  loadingDetail: boolean
  releases: Release[]
  selectedRelease: Release | null
  selectedReleaseInModal: Release | null
  scrollPosition: number
  currentReleaseInformation: Release | null
  status: string
  error?: unknown
}

const initialState: ReleasesState = {
  queryKey: null,
  requestId: null,
  archiveToFilterEnabled: false,
  queryArchived: false,
  pendingArchiveIds: [],
  page: 0,
  totalItemsCount: null,
  sort: '-date',
  filter: null,
  artist: null,
  title: null,
  label: null,
  version: null,
  market: null,
  days: null,
  loading: false,
  loadingDetail: false,
  releases: [],
  selectedRelease: null,
  selectedReleaseInModal: null,
  scrollPosition: 0,
  currentReleaseInformation: null,
  status: 'idle'
}

const releasesSlice = createSlice({
  name: 'releases',
  initialState,
  reducers: {
    requestStarted: (
      state,
      action: PayloadAction<{
        queryKey: string
        requestId: string
        page: number
        archiveToFilterEnabled: boolean
        archived: boolean
      }>
    ) => {
      const { queryKey, requestId, page, archiveToFilterEnabled } =
        action.payload
      if (page === 1) {
        state.releases = []
        state.page = 0
        state.totalItemsCount = null
        state.scrollPosition = 0
      }
      state.queryKey = queryKey
      state.requestId = requestId
      state.archiveToFilterEnabled = archiveToFilterEnabled
      state.queryArchived = action.payload.archived
      state.loading = true
      state.status = 'loading'
      state.error = null
    },
    requestSucceeded: (
      state,
      action: PayloadAction<{
        requestId: string
        releases: Release[]
        page: number
        totalItemsCount: number
      }>
    ) => {
      if (state.requestId !== action.payload.requestId) return
      const { releases, page, totalItemsCount } = action.payload
      const items = page === 1 ? releases : [...state.releases, ...releases]
      state.releases = items.filter(
        (item, index) =>
          items.findIndex((other) => other.releaseid === item.releaseid) ===
          index
      )
      state.page = page
      state.totalItemsCount = totalItemsCount
      state.loading = false
      state.status = 'done'
    },
    requestFailed: (
      state,
      action: PayloadAction<{
        requestId: string
        error: string
      }>
    ) => {
      if (state.requestId !== action.payload.requestId) return
      state.error = action.payload.error
      state.loading = false
      state.status = 'error'
    },
    archiveStarted: (state, action: PayloadAction<string>) => {
      state.pendingArchiveIds.push(action.payload)
    },
    archiveFinished: (state, action: PayloadAction<string>) => {
      state.pendingArchiveIds = state.pendingArchiveIds.filter(
        (id) => id !== action.payload
      )
    },
    sortBy: (state, action: PayloadAction<string>) => {
      state.sort = state.sort === action.payload ? null : action.payload
    },
    removeRelease: (state, action: PayloadAction<Release>) => {
      state.releases = state.releases.filter(
        (item) => item.releaseid !== action.payload.releaseid
      )
    },
    addRelease: (state, action: PayloadAction<Release>) => {
      state.releases.unshift(action.payload)
    },
    loadingReleases: (state) => {
      state.loading = true
    },
    loadingReleasesSuccess: {
      prepare(releases: Release[], page: number, totalItemsCount: number) {
        return { payload: { releases, page, totalItemsCount } }
      },
      reducer(
        state,
        action: PayloadAction<{
          releases: Release[]
          page: number
          totalItemsCount: number
        }>
      ) {
        state.releases = action.payload.releases
        state.page = action.payload.page
        state.totalItemsCount = action.payload.totalItemsCount
        state.loading = false
      }
    },
    loadingReleasesFail: (state, action: PayloadAction<unknown>) => {
      state.error = action.payload
      state.loading = false
    },
    loadingReleaseDetail: (state) => {
      state.loadingDetail = true
    },
    selectDisplayRelease: (state, action: PayloadAction<Release | null>) => {
      state.selectedRelease = action.payload
    },
    toggleReleaseInModal: (state, action: PayloadAction<Release | null>) => {
      state.selectedReleaseInModal = action.payload
    },
    reset: () => initialState,
    refreshList: (state) => {
      state.page = 0
      state.totalItemsCount = null
      state.releases = []
      state.requestId = null
      state.queryKey = null
      state.loading = false
    },
    setReleaseGalleryScrollPosition: (state, action: PayloadAction<number>) => {
      state.scrollPosition = action.payload
    },
    setCurrentReleaseInformation: (
      state,
      action: PayloadAction<Release | null>
    ) => {
      state.currentReleaseInformation = action.payload
    },
    setStatus: (state, action: PayloadAction<string>) => {
      state.status = action.payload
    },
    updateRelease: (state, action: PayloadAction<Release>) => {
      const release = action.payload
      state.releases = state.releases.map((r) =>
        r.releaseid === release.releaseid ? { ...r, ...release } : r
      )
      state.currentReleaseInformation = release
      if (state.selectedReleaseInModal?.releaseid === release.releaseid) {
        state.selectedReleaseInModal = {
          ...state.selectedReleaseInModal,
          ...release
        }
      }
    },
    setReleases: {
      prepare(releases: Release[], page: number, totalItemsCount: number) {
        return { payload: { releases, page, totalItemsCount } }
      },
      reducer(
        state,
        action: PayloadAction<{
          releases: Release[]
          page: number
          totalItemsCount: number
        }>
      ) {
        state.releases = action.payload.releases
        state.page = action.payload.page
        state.totalItemsCount = action.payload.totalItemsCount
        state.loading = false
      }
    },
    appendReleases: {
      prepare(releases: Release[], page: number, totalItemsCount: number) {
        return { payload: { releases, page, totalItemsCount } }
      },
      reducer(
        state,
        action: PayloadAction<{
          releases: Release[]
          page: number
          totalItemsCount: number
        }>
      ) {
        state.releases.push(...action.payload.releases)
        state.page = action.payload.page
        state.totalItemsCount = action.payload.totalItemsCount
        state.loading = false
      }
    }
  }
})

export const releasesActions = releasesSlice.actions
export const releasesReducer = releasesSlice.reducer
export default releasesActions
